import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/ui/PageHeader";
import FilterBar from "../../components/ui/FilterBar";
import leaveService from "../../services/leaveService";
import { useToast } from "../../contexts/ToastContext";
import LeaveApprovedDetailModal from "../../components/leave/LeaveApprovedDetailModal";
import { FileText, User, Clock, RefreshCw, Upload, Eye ,Ban} from "lucide-react";
import "../../styles/dashboard.css";

const LeaveHistoryRH = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: "",
    leave_type_id: "",
    search: ""
  });
  
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [attachmentCounts, setAttachmentCounts] = useState({});
  const [importingExcel, setImportingExcel] = useState(false);
  const excelInputRef = useRef(null);

  useEffect(() => {
    loadLeaveTypes();
  }, []);

  useEffect(() => {
    loadHistory();
  }, [filters.year, filters.month, filters.leave_type_id]);

  const loadLeaveTypes = async () => {
    try {
      const types = await leaveService.getLeaveTypes();
      setLeaveTypes(types || []);
    } catch (err) {
      console.error("Error loading leave types:", err);
    }
  };

  
  const loadHistory = async () => {
    setLoading(true);

    try {
      const res = await leaveService.getApprovedHistory({
        year: filters.year || null,
        month: filters.month || null,
        leave_type_id: filters.leave_type_id || null,
      });

      const requests = Array.isArray(res) ? res : [];
      setHistory(requests);

      // Charger les pièces jointes complémentaires de chaque demande
      const results = await Promise.all(
        requests.map(async (leave) => {
          try {
            const attachments = await leaveService.getAdditionalAttachments(leave.id);
            return [
              leave.id,
              Array.isArray(attachments) ? attachments.length : 0,
            ];
          } catch (error) {
            console.error(
              `Erreur chargement des pièces jointes du congé ${leave.id}:`,
              error
            );
            return [leave.id, 0];
          }
        })
      );

      setAttachmentCounts(Object.fromEntries(results));
    } catch (err) {
      console.error("Erreur chargement historique :", err);
      toast.error("Impossible de charger l'historique des congés.");
    } finally {
      setLoading(false);
    }
  };

  // Import des congés historiques validés depuis Excel/CSV.
  // Le service doit envoyer le fichier à l'endpoint backend d'import RH.
  const handleExcelSelection = async (event) => {
    const file = event.target.files?.[0];
    // Permet de sélectionner à nouveau le même fichier après une erreur.
    event.target.value = "";
    if (!file) return;

    const validExtension = /\.(xlsx|xls|csv)$/i.test(file.name);
    if (!validExtension) {
      toast.error("Format invalide. Sélectionnez un fichier Excel (.xlsx, .xls) ou CSV (.csv).");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Le fichier dépasse la taille maximale de 15 Mo.");
      return;
    }

    if (typeof leaveService.importHistoricalLeaves !== "function") {
      toast.error(
        "Le bouton est prêt, mais l'API d'import n'est pas encore configurée dans leaveService."
      );
      console.error(
        "Ajouter leaveService.importHistoricalLeaves(file) et l'endpoint backend d'import historique RH."
      );
      return;
    }

    const confirmed = window.confirm(
      `Importer le fichier « ${file.name} » ?\n\n` +
      "L'import doit contrôler les collaborateurs, les types, les dates et les doublons avant d'enregistrer les congés historiques."
    );
    if (!confirmed) return;

    setImportingExcel(true);
    try {
const result = await leaveService.importHistoricalLeaves(file);
const imported =
  result?.imported ?? result?.importees ?? result?.created ?? 0;

const skipped =
  result?.skipped ?? result?.doublons_ignores ?? result?.ignored ?? 0;

const errors = Array.isArray(result?.errors)
  ? result.errors.length
  : Array.isArray(result?.erreurs)
    ? result.erreurs.length
    : Number(result?.lignes_en_erreur ?? 0);
      await loadHistory();
    } catch (err) {
      console.error("Erreur import historique Excel :", err);
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Erreur inconnue";
      toast.error(`Échec de l'import Excel : ${message}`);
    } finally {
      setImportingExcel(false);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      year: new Date().getFullYear(),
      month: "",
      leave_type_id: "",
      search: ""
    });
  };

  const filteredHistory = history.filter(h => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (!h.employee_name?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const handleRowClick = (leave) => {
    setSelectedLeave(leave);
    setShowDetailModal(true);
  };

  const handleAddAttachment = (leaveId, e) => {
    e.stopPropagation();
    navigate(`/rh/leaves/${leaveId}/attachment`);
  };

  const handleModalClose = (wasUpdated) => {
    setShowDetailModal(false);
    setSelectedLeave(null);
    if (wasUpdated) {
      toast.success("Pièces jointes mises à jour.");
      loadHistory();
    }
  };

  const getLeaveTypeColor = (leaveTypeCode) => {
    const colors = {
      "ANNUEL": { bg: "var(--status-info-bg)", text: "var(--status-info-text)" },
      "MALADIE": { bg: "var(--status-rejected-bg)", text: "var(--status-rejected-text)" },
      "RECUPERATION": { bg: "var(--bg-subtle)", text: "var(--text-secondary)" },
      "MARIAGE": { bg: "var(--status-pending-bg)", text: "var(--status-pending-text)" },
      "NOUVEAU_NE": { bg: "#e9d5ff", text: "#7c3aed" }
    };
    return colors[leaveTypeCode] || { bg: "var(--bg-subtle)", text: "var(--text-main)" };
  };

  return (
    <div style={{ padding: "24px" }}>
      <PageHeader
        title="Historique des Congés Approuvés"
        subtitle="Consultez les archives de tous les congés validés et complétez les dossiers (certificats médicaux)."
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <input
              ref={excelInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              onChange={handleExcelSelection}
              style={{ display: "none" }}
              aria-label="Sélectionner un fichier Excel ou CSV de congés historiques"
            />
            <button
              type="button"
              onClick={() => excelInputRef.current?.click()}
              disabled={importingExcel}
              className="btn btn-primary"
              style={{ display: "flex", alignItems: "center", gap: "8px", opacity: importingExcel ? 0.7 : 1 }}
              title="Importer les congés historiques validés depuis Excel ou CSV"
            >
              <Upload size={16} />
              <span>{importingExcel ? "Import en cours..." : "Importer Excel / CSV"}</span>
            </button>
            <button
              type="button"
              onClick={loadHistory}
              disabled={loading || importingExcel}
              className="btn btn-outline"
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <RefreshCw size={16} />
              <span>Actualiser</span>
            </button>
          </div>
        }
      />

      {/* Filter Bar */}
      <FilterBar
        searchValue={filters.search}
        onSearchChange={(val) => setFilters({ ...filters, search: val })}
        placeholder="Rechercher un employé..."
        filters={[
          {
            id: "year",
            label: "Année",
            value: String(filters.year),
            onChange: (val) => setFilters({ ...filters, year: val }),
            options: [2024, 2025, 2026, 2027].map(y => ({ value: String(y), label: String(y) }))
          },
          {
            id: "month",
            label: "Mois",
            value: String(filters.month),
            onChange: (val) => setFilters({ ...filters, month: val }),
            options: [
              { value: "", label: "Tous les mois" },
              ...Array.from({ length: 12 }).map((_, i) => ({
                value: String(i + 1),
                label: new Date(0, i).toLocaleString('fr', { month: 'long' })
              }))
            ]
          },
          {
            id: "leave_type",
            label: "Type",
            value: String(filters.leave_type_id),
            onChange: (val) => setFilters({ ...filters, leave_type_id: val }),
            options: [
              { value: "", label: "Tous les types" },
              ...leaveTypes.map(t => ({ value: String(t.id), label: t.name }))
            ]
          }
        ]}
        onReset={handleResetFilters}
      />

      {/* History Table */}
      <div className="ui-card" style={{ overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
            Chargement de l'historique des congés...
          </div>
        ) : filteredHistory.length === 0 ? (
          <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
            Aucun congé approuvé ne correspond à vos critères.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
              <thead>
                <tr style={{ backgroundColor: "var(--bg-subtle)", borderBottom: "1px solid var(--border)", color: "var(--text-muted)", fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left" }}>Employé</th>
                  <th style={{ padding: "12px 16px", textAlign: "left" }}>Type de congé</th>
                  <th style={{ padding: "12px 16px", textAlign: "left" }}>Période</th>
                  <th style={{ padding: "12px 16px", textAlign: "center" }}>Durée</th>
                  <th style={{ padding: "12px 16px", textAlign: "left" }}>Validé par</th>
                  <th style={{ padding: "12px 16px", textAlign: "left" }}>Date validation</th>
                  <th style={{ padding: "12px 16px", textAlign: "center" }}>Pièces jointes</th>
                  <th style={{ padding: "12px 16px", textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((leave) => {
                  const typeColor = getLeaveTypeColor(leave.leave_type_code);
                  //const hasMainAttachment = !!leave.attachment_path;
                  const hasMainAttachment = Boolean(
                    leave.attachment_path ||
                    leave.attachment_original_name ||
                    leave.has_attachment
                  );

                  const additionalCount = attachmentCounts[leave.id] || 0;

                  const totalAttachments =
                    (hasMainAttachment ? 1 : 0) + additionalCount;

                  return (
                    <tr
                      key={leave.id}
                      onClick={() => handleRowClick(leave)}
                      style={{ borderBottom: "1px solid var(--border)", cursor: "pointer", transition: "var(--transition-fast)" }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--primary-light)"}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                    >
                      <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", color: "var(--text-main)" }}>
                          <User size={16} style={{ color: "var(--text-muted)" }} />
                          {leave.employee_name}
                        </div>
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                        <span style={{
                          backgroundColor: typeColor.bg,
                          color: typeColor.text,
                          padding: "4px 10px",
                          borderRadius: "var(--radius-pill)",
                          fontSize: "0.8rem",
                          fontWeight: "600"
                        }}>
                          {leave.leave_type_name}
                        </span>
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle", fontSize: "0.88rem", color: "var(--text-secondary)" }}>
                        Du {new Date(leave.start_date).toLocaleDateString('fr-FR')} au {new Date(leave.end_date).toLocaleDateString('fr-FR')}
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "center", verticalAlign: "middle", fontWeight: "700", color: "var(--text-main)" }}>
                        {leave.duration_days} j
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle", color: "var(--text-muted)", fontSize: "0.88rem" }}>
                        {leave.hr_name || "N/A"}
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                        {leave.hr_decision_date ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "0.82rem", color: "var(--text-muted)" }}>
                            <Clock size={14} />
                            {new Date(leave.hr_decision_date).toLocaleDateString('fr-FR')}
                          </div>
                        ) : "-"}
                      </td>
                      
<td
  style={{
    padding: "14px 16px",
    textAlign: "center",
    verticalAlign: "middle",
  }}
>
  {totalAttachments > 0 ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        handleRowClick(leave);
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        border: "none",
        background: "transparent",
        color: "var(--primary)",
        cursor: "pointer",
        fontSize: "0.85rem",
        fontWeight: "600",
      }}
      title="Voir les pièces jointes"
    >
      <FileText size={16} />
      {totalAttachments} fichier
      {totalAttachments > 1 ? "s" : ""}
    </button>
  ) : (
    <span
      style={{
        color: "var(--text-light)",
        fontSize: "0.82rem",
      }}
    >
      Aucune
    </span>
  )}
</td>

                      <td style={{ padding: "14px 16px", textAlign: "center", verticalAlign: "middle" }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(leave);
                          }}
                          className="btn btn-outline"
                          style={{
                            padding: "8px 16px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "0.85rem"
                          }}
                          title="Voir détails"
                        >
                          <Eye size={16} />
                          <span>Voir</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showDetailModal && selectedLeave && (
        <LeaveApprovedDetailModal
          leave={selectedLeave}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default LeaveHistoryRH;

